import dayjs from 'dayjs'
import formatIntegerBySpace from '../../../helpers/formatters/format-integer-by-space'
import UserAudit from '../../../types/api/components/audit/user-audit'
import PrintJob from '../../../types/api/print-job'

function getUserAuditCreatorUsername(userAudit?: UserAudit): string | undefined {
    if (!userAudit) return undefined

    return (
        userAudit.created_by_permissions_user?.username ||
        userAudit.updated_by_permissions_user?.username
    )
}

export function getConsumptionSavedByUsername(printJob: PrintJob): string | undefined {
    for (const consumption of printJob.material_consumptions || []) {
        const username = getUserAuditCreatorUsername(consumption.user_audit)
        if (username) return username
    }

    const consumptions = [...(printJob.material_consumptions || [])].sort(
        (a, b) =>
            dayjs(b.updatedAt || b.createdAt).valueOf() -
            dayjs(a.updatedAt || a.createdAt).valueOf()
    )

    return getUserAuditCreatorUsername(consumptions[0]?.user_audit)
}

export function getUniqueMachines(printJob: PrintJob) {
    const machines = new Map<string, NonNullable<PrintJob['print_job_executors']>[number]['machine']>()

    for (const executor of printJob.print_job_executors || []) {
        if (executor.machine?.documentId) {
            machines.set(executor.machine.documentId, executor.machine)
        }
    }

    return [...machines.values()]
}

export function parseAmountKm(value: unknown): number {
    if (typeof value === 'string') {
        const normalized = value.trim().replace(/\s/g, '').replace(',', '.')

        if (!normalized) return 0

        const numeric = Number(normalized)

        return Number.isFinite(numeric) ? numeric : 0
    }

    return toMeters(value)
}

export function roundAmountKm(value: unknown): number {
    return Math.round(parseAmountKm(value) * 1000) / 1000
}

export function toMeters(value: unknown): number {
    const numeric = Number(value)

    return Number.isFinite(numeric) ? numeric : 0
}

export function parseLayerNumber(value: unknown): number | undefined {
    if (value === 'OneLayer') return 1
    if (value === 'TwoLayer') return 2
    if (value === 'ThreeLayer') return 3
    const numeric = Number(value)
    if (Number.isFinite(numeric) && numeric > 0) return numeric
    return undefined
}

export function formatLayerLabel(layerNumber: unknown): string {
    const layer = parseLayerNumber(layerNumber)
    return layer ? `${layer} слой` : '—'
}

export function sumMaterialConsumptionLengthKm(
    consumptions: {amount?: unknown; layerNumber?: unknown; LayerNumber?: unknown}[] | undefined
): number {
    return (consumptions || []).reduce((sum, item) => {
        const layer = parseLayerNumber(item.layerNumber)
        if (layer !== 1) return sum
        return sum + parseAmountKm(item.amount)
    }, 0)
}

export function getPrintJobMileageIncrementMeters(printJob: PrintJob): number {
    return materialLengthKmToMeters(sumMaterialConsumptionLengthKm(printJob.material_consumptions))
}

export function hasSavedMaterialConsumptions(printJob: PrintJob): boolean {
    return Boolean(printJob.material_consumptions?.length)
}

/** Пробег строки: снимок printJob.machineCounter на момент сохранения расхода */
export function getCumulativeMileageMeters(printJob: PrintJob): number | undefined {
    if (!hasSavedMaterialConsumptions(printJob)) return undefined

    const snapshot = toMeters(printJob.machineCounter)

    return snapshot || undefined
}

export function getRowMileageDisplay(printJob: PrintJob): string {
    if (!hasSavedMaterialConsumptions(printJob)) return '—'

    const totalMeters = getCumulativeMileageMeters(printJob)

    if (totalMeters == null) return '—'

    return formatMileageDisplay(totalMeters, getPrintJobMileageIncrementMeters(printJob))
}

/**
 * Новый глобальный счётчик станка после сохранения расхода.
 * initialConsumptionMeters фиксируется при открытии формы, чтобы не учитывать refetch.
 */
export function computeMachineCounterAfterSave(
    currentMachineCounter: number,
    initialConsumptionMeters: number,
    nextConsumptionMeters: number
): number | null {
    if (nextConsumptionMeters <= 0) return null

    if (initialConsumptionMeters > 0) {
        const delta = nextConsumptionMeters - initialConsumptionMeters

        if (delta <= 0) return null

        return currentMachineCounter + delta
    }

    return currentMachineCounter + nextConsumptionMeters
}

export function getSavedConsumptionMeters(printJob: PrintJob): number {
    return getPrintJobMileageIncrementMeters(printJob)
}

/** Метраж материалов в км → метры для счётчика станка */
export function materialLengthKmToMeters(lengthKm: unknown): number {
    return Math.round(parseAmountKm(lengthKm) * 1000)
}

export function formatMileageMeters(value: number): string {
    return formatIntegerBySpace(Math.round(value))
}

export function formatMileageDisplay(totalMeters: number, incrementMeters: number): string {
    if (!totalMeters && !incrementMeters) return '—'
    if (!incrementMeters) return formatMileageMeters(totalMeters)

    return `${formatMileageMeters(totalMeters)} (+${formatMileageMeters(incrementMeters)})`
}
